import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { io, Socket } from "socket.io-client";
import { IApplicationState } from "@/store-redux";
import { addNotification, setSocketConnected } from "@/store-redux/slices/notificationSlice";
import { showMessage } from "@/store-redux";
import { INotification, NotificationType } from "@/common/types/notification";
import { KeyTokenName } from "@/constants";

const SERVER_URL = import.meta.env.VITE_API_BASE as string;

/** Map notification type → toast message severity */
const severityMap: Record<NotificationType, "success" | "info" | "warning" | "error"> = {
  [NotificationType.ASSIGNMENT_NEW]: "info",
  [NotificationType.ASSIGNMENT_SUBMITTED]: "info",
  [NotificationType.ASSIGNMENT_TASK_SCORED]: "success",
  [NotificationType.ASSIGNMENT_CLOSED]: "warning",
  [NotificationType.ASSIGNMENT_DUE_SOON]: "warning",
  [NotificationType.ENERGY_LOW]: "warning",
  [NotificationType.ENERGY_REFILLED]: "success",
  [NotificationType.PAYMENT_SUCCESS]: "success",
  [NotificationType.PAYMENT_FAILED]: "error",
  [NotificationType.SUBSCRIPTION_EXPIRING]: "warning",
  [NotificationType.SUBSCRIPTION_EXPIRED]: "error",
  [NotificationType.SYSTEM_ANNOUNCEMENT]: "info",
};

/**
 * Connects to Socket.IO with JWT auth and listens for 'notification' events.
 * Mount this once at app level (inside AppSelector after auth is available).
 */
export const useNotificationSocket = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state: IApplicationState) => state.currentUser);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const token = localStorage.getItem(KeyTokenName);
    if (!token || !currentUser?.data?._id) return;

    // Connect with JWT auth
    const socket = io(SERVER_URL, {
      transports: ["websocket"],
      auth: { token },
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("[Socket] Connected for notifications:", socket.id);
      dispatch(setSocketConnected(true));
    });

    socket.on("notification", (data: INotification) => {
      // 1. Add to Redux store (notification center)
      dispatch(addNotification(data));

      // 2. Show toast via existing systemMessage reducer
      const severity = severityMap[data.type] ?? "info";
      dispatch(showMessage(data.title, severity));
    });

    socket.on("disconnect", () => {
      console.log("[Socket] Disconnected from notification socket");
      dispatch(setSocketConnected(false));
    });

    socket.on("connect_error", (err) => {
      console.warn("[Socket] Connection error:", err.message);
      dispatch(setSocketConnected(false));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      dispatch(setSocketConnected(false));
    };
  }, [currentUser?.data?._id, dispatch]);
};
